/** @format */

import { doc, getDoc } from "firebase/firestore/lite";
import { useFormik } from "formik";
import React, { type FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useGlobalState, useSchemaValidation } from "senf-shared";
import { db } from "senf-shared/auth/firebase";

import { toSentenceCase } from "senf-shared/utils/convertCase";
import i18n from "senf-shared/utils/i18n";
import { fetchProjectSpecificData } from "../../../util/demographicUtils";
import Loader from "../../atoms/animations/Loader";
import Input from "../../atoms/inputs/Input";
import Switch from "../../atoms/switch/Switch";
import Avatar from "../../core/avatar/Avatar";
import Button from "../../core/button/Button";
import Seperator from "../../core/seperator/Seperator";
import DemographicsForm from "../demographics/DemographicsForm";
import type { AuthAddDetailsProps } from "./AuthAddDetails.types";

const AuthAddDetails: FC<AuthAddDetailsProps> = ({
  user,
  authHandler,
  project,
}) => {
  const { t } = useTranslation();
  const { addUserDetails } = useSchemaValidation();
  const [translation] = useGlobalState().translationsState;
  const [image, setImage] = useState(user?.photoURL);
  const [imageEvent, setImageEvent] = useState<Event | null>(null);

  const [initialValues, setInitialValues] = useState({
    handle: user?.isAnonymous ? "Gastnutzer" : user?.handle || "",
    postcode: user?.postcode || "",
    age: user?.age || "",
    gender: user?.gender || "",
    newsletter: user?.newsletter || false,
  });

  useEffect(() => {
    const loadProjectSpecificData = async () => {
      const participantData = await fetchProjectSpecificData(project, user);
      if (participantData) {
        setInitialValues((prevValues) => ({
          ...prevValues,
          ...participantData,
        }));
      }
    };

    loadProjectSpecificData();
  }, [project, user]);

  const formik = useFormik({
    initialValues,
    validationSchema: addUserDetails(project, translation),
    validateOnMount: true,
    validateOnChange: true,
    validateOnBlur: true,
    enableReinitialize: true,
    onSubmit: (values) => {
      console.log("submit");
    },
  });

  useEffect(() => {
    formik.validateForm();
  }, []);

  useEffect(() => {
    if (formik.values.handle) {
      formik.setTouched((prevTouched) => ({ ...prevTouched, handle: true }));
    }
    if (formik.values.postcode) {
      formik.setTouched((prevTouched) => ({ ...prevTouched, postcode: true }));
    }
  }, [formik.values.handle, formik.values.postcode]);

  const setDisabledNotice = () => {
    try {
      return toSentenceCase(
        new Intl.ListFormat(i18n.resolvedLanguage, {
          style: "long",
          type: "conjunction",
        }).format(Object.values(formik?.errors || {}))
      );
    } catch (error) {
      return t("global:fillInAllFields");
    }
  };

  return (
    <div className="relative mx-[6%] my-[19px] mb-[10%] flex flex-col">
      <div className="flex flex-col">
        {!user?.isAnonymous ? (
          <span className="heading-2xl">
            {t("auth:addDetails.title1")}
            <br />
            {t("auth:addDetails.title2")}
          </span>
        ) : (
          <>
            <span className="heading-2xl">{t("global:demographicData")}</span>
            <span className="body-sm text-text-subdued mt-2 w-80">
              {t("auth:authOptions.demographicDataDescription")}
            </span>
          </>
        )}
      </div>

      <div className="my-8 flex flex-col gap-4">
        {!user?.isAnonymous && (
          <div className="flex items-start gap-3.5">
            <label htmlFor="authAddDetailsImageUploader">
              {image || !authHandler?.authLoading ? (
                <div className="mt-7">
                  <Avatar
                    alt={formik.values?.handle}
                    size="md"
                    src={image}
                    notLoggedIn={!image}
                  />
                </div>
              ) : (
                <Loader />
              )}
            </label>

            <input
              type="file"
              onChange={(e) => {
                if (e.target.files && e.target.files[0]) {
                  if (e.target.files[0].type.match(/image\/*/) != null) {
                    setImageEvent(e);
                    setImage(URL.createObjectURL(e.target.files[0]));
                  } else {
                    console.error("Please select a valid image file.");
                  }
                }
              }}
              className="hidden"
              id="authAddDetailsImageUploader"
            />

            <Input
              name="handle"
              type="text"
              placeholder={t("global:username")}
              onChange={formik?.handleChange}
              onBlur={formik?.handleBlur}
              value={formik?.values.handle}
              error={formik?.touched.handle && Boolean(formik?.errors.handle)}
              note={formik?.touched.handle && formik?.errors.handle}
              label={t("global:username")}
            />
          </div>
        )}

        {!user.isAnonymous && (
          <div className="flex flex-col gap-1">
            <div className="flex items-baseline gap-1.5">
              <span className="text-black-80tra text-[0.9rem] font-bold">
                {t("global:demographicData")}
              </span>
              <span className="text-grey-900 text-sm">
                ({t("global:notPublic")})
              </span>
            </div>
            <span className="text-grey-900 text-sm">
              {t("auth:authOptions.demographicDataDescription")}
            </span>
          </div>
        )}

        <DemographicsForm
          formik={formik}
          project={project}
          user={user}
          isAuthFlow={true}
        />

        
      </div>

      <div className="sticky bottom-0 w-full">
        <Button
          className="w-full"
          size="lg"
          variant="primary"
          onClick={() => {
            if (!imageEvent) {
              authHandler.submitEditDetails(formik, project);
              return;
            }
            authHandler.handleImageUpload(imageEvent).then(() => {
              authHandler.submitEditDetails(formik, project);
            });
          }}
          loading={authHandler?.loadingAuth === "edit"}
          disabled={
            !formik.isValid ||
            !formik.values.age ||
            !formik.values.gender ||
            authHandler?.loadingAuth === "edit"
          }
          disabledNotice={
           setDisabledNotice()
          }
          text={t("global:save")}
        />
      </div>
    </div>
  );
};

export default AuthAddDetails;
